/**
 * layoutSpec.ts — the Phaser gameplay layout truth.
 *
 * PROVENANCE: transcribed from the layout-editor export
 * `TheDominoGame.zip / project.kpe` (scene "GameLayoutReference",
 * 1000x1000 design space, all objects center-origin). That file is the
 * single source of truth for gameplay-scene positions and dimensions —
 * when the design changes, update these constants from the new export;
 * never tune them ad hoc in scene code.
 *
 * All values are DESIGN UNITS in a 1000x1000 square whose center (500,500)
 * is the table center. At runtime the scene maps design units to canvas
 * pixels with a single uniform factor k (see GameScene.computeLayout):
 *
 *   k = drawnTableSize / DESIGN_SIZE
 *   canvasX = tableCX + (designX - 500) * k     (same for Y)
 *
 * Anchoring to the drawn table (rather than the raw square) means sub-pixel
 * rounding of the table size can never desync the furniture from the table.
 *
 * Layout only: nothing here affects rules, networking, animations or the
 * board engine.
 */

/** Side of the design square. */
export const DESIGN_SIZE = 1000;

/** Board-engine grid constant mirror: the table image spans 19 grid cells
 *  x TABLE_VISUAL_SCALE (see boardRenderConfig). One grid unit in design
 *  space — used to convert spec sizes into engine-relative factors. */
export const DESIGN_UNIT = DESIGN_SIZE / (19 * 1.15); // ≈ 45.767

export const LAYOUT_SPEC = {
  /** table-green.png centered, filling the design square edge-to-edge. */
  table: { x: 500, y: 500, size: 1000 },

  /** tiles-holder rack image under the local hand. */
  holder: { x: 500, y: 940, w: 728.6, h: 127.1 },

  /** Local hand: 7 vertical tiles, fixed slot pitch, centered on x=500. */
  hand: {
    y: 895,
    startX: 221,
    pitch: 93,
    slots: 7,
    tileW: 81.9,   // on-screen width of a vertical hand tile
    tileH: 163.8,  // on-screen height (2:1 portrait)
  },

  /** Team-colored glow behind the local hand zone. */
  handBg: { x: 495, y: 885, w: 676.2, h: 203.4 },

  /** PASS button (replaces the knock-fist position/size on desktop). */
  pass: { x: 912, y: 784, size: 152 },

  // ── Phase 2 (opponent panels) — transcribed now, wired later ─────────────
  panelTop: {
    bg:     { x: 460, y: 50, w: 394.4, h: 84.8 },
    avatar: { x: 306, y: 47, w: 71.8, h: 78.7 },
    backTiles: { fixed: 50, start: 374, pitch: 42, count: 7, w: 76.8, h: 38.4, rotated: true },
  },
  panelLeft: {
    bg:     { x: 49, y: 455, w: 93.7, h: 397 },
    avatar: { x: 49, y: 300, w: 71.8, h: 79.8 },
    backTiles: { fixed: 50, start: 374, pitch: 42, count: 7, w: 76.8, h: 38.4, rotated: false },
  },
  panelRight: {
    bg:     { x: 943, y: 456, w: 93.7, h: 397 },
    avatar: { x: 941, y: 300, w: 71.8, h: 79.8 },
    backTiles: { fixed: 943, start: 374, pitch: 42, count: 7, w: 76.8, h: 38.4, rotated: false },
  },

  /**
   * Spectator-only fourth panel. NOT part of the original kpe layout export —
   * the bottom seat is normally the LOCAL PLAYER's own hand tray (see `hand`,
   * `holder`, `handBg`, `pass` above), which a spectator (no seat of their
   * own) never has. Mirrors panelTop exactly across the table's horizontal
   * axis (y' = 1000 - y; x unchanged) so it is a principled reflection of an
   * already-approved element, not an ad hoc guess. Used only when spectating
   * — see DesktopPlayerCards.update().
   */
  panelBottom: {
    bg:     { x: 460, y: 950, w: 394.4, h: 84.8 },
    avatar: { x: 306, y: 953, w: 71.8, h: 78.7 },
    backTiles: { fixed: 950, start: 374, pitch: 42, count: 7, w: 76.8, h: 38.4, rotated: true },
  },
} as const;

/** Hand tile width expressed as a multiple of the engine grid unit — feeds
 *  Hand.ts's existing `unit * handScale` sizing so hand tiles land at
 *  exactly the spec's on-screen size. 81.9 / 45.767 ≈ 1.7895. */
export const HAND_SCALE_SPEC = LAYOUT_SPEC.hand.tileW / DESIGN_UNIT;

/** Gap between adjacent hand slots in design units (pitch minus tile width). */
export const HAND_SLOT_GAP_SPEC = LAYOUT_SPEC.hand.pitch - LAYOUT_SPEC.hand.tileW; // 11.1

/** Map a design-space coordinate onto the canvas. */
export function designToCanvas(
  design: number, designCenter: number, canvasCenter: number, k: number,
): number {
  return canvasCenter + (design - designCenter) * k;
}

// ── Opponent card geometry (design units) ──────────────────────────────────
// The spec's panel bg sizes are the SEVEN-tile case. These derive the card
// for any live count so it hugs its contents, and keep the tile run centred
// on the table axis so played tiles collapse inward rather than off one end.

/** On-screen along-axis extent of one face-down back tile. */
export const OPP_TILE_ALONG = 38.4;
/** On-screen across-axis extent of one face-down back tile. */
export const OPP_TILE_ACROSS = 76.8;
/** Centre-to-centre spacing along the run (spec: 42 → 3.6 gap). */
export const OPP_TILE_PITCH = 42;
/** Padding between card edge and its contents. */
export const OPP_CARD_PAD = 8;
/**
 * Card thickness, identical for all three opponents so the panels are the
 * same size. max(largest avatar across-extent 78.7, tile across 76.8) + 2*pad.
 * Reproduces the spec's side thickness (93.7) to within 1 design unit.
 */
export const OPP_CARD_THICK = 94.7;

/**
 * Canonical avatar along-extent, shared by all three opponents so their cards
 * come out dimensionally IDENTICAL. The raw spec differs slightly per panel
 * (top w=71.8, side h=79.8); one canonical extent removes that. The avatar is
 * rendered at its true asset size — only the CARD MATH uses this, with padding
 * absorbing the small difference.
 */
export const OPP_AVATAR_ALONG_EXTENT = 76;

/**
 * Gap (design units) between the avatar's inner edge and the first tile.
 * The avatar tracks the tile run — it sits this far ahead of runStart — so it
 * moves inward together with the tiles as they are played. Chosen so that at
 * the 7-tile count the avatar lands on ~303, matching the spec's own placement
 * (side 300 / top 306): runStart(7)=354.8 → 354.8 − 13.8 − 38 = 303.
 */
export const OPP_AVATAR_RUN_GAP = 13.8;

export interface OpponentCardGeom {
  /** Length of the tile run along the card's long axis. 0 when no tiles. */
  runLen: number;
  /** Leading edge of the tile run (design units, along-axis). */
  runStart: number;
  /** Centre of the first tile — successive tiles step by OPP_TILE_PITCH. */
  firstTileCentre: number;
  /** Avatar centre along the long axis — tracks the run, so it moves inward
   *  with the tiles as they are played. */
  avatarAlongCentre: number;
  /** Card length along its long axis. */
  cardAlong: number;
  /** Card centre along its long axis. */
  cardAlongCentre: number;
}

/**
 * Card geometry for a live tile count.
 *
 * The run is centred on the design axis (500) and shrinks inward; the avatar
 * sits OPP_AVATAR_RUN_GAP ahead of the run's leading edge, so avatar and tiles
 * move together as tiles are played; the card spans avatar → run far edge plus
 * padding, hugging its contents at every count.
 */
export function computeOpponentCard(
  count: number,
  avatarAlongExtent: number,
  gap = OPP_AVATAR_RUN_GAP,
): OpponentCardGeom {
  const n      = Math.max(0, count);
  const runLen = n > 0 ? n * OPP_TILE_PITCH - (OPP_TILE_PITCH - OPP_TILE_ALONG) : 0;
  const centre = DESIGN_SIZE / 2;

  const runStart = centre - runLen / 2;
  const runEnd   = centre + runLen / 2;

  // Avatar leads the run (or centres on the axis when the hand is empty).
  const avatarAlongCentre = n > 0
    ? runStart - gap - avatarAlongExtent / 2
    : centre;

  const leading = (avatarAlongCentre - avatarAlongExtent / 2) - OPP_CARD_PAD;
  const far     = (n > 0 ? runEnd : avatarAlongCentre + avatarAlongExtent / 2) + OPP_CARD_PAD;

  return {
    runLen,
    runStart,
    firstTileCentre: runStart + OPP_TILE_ALONG / 2,
    avatarAlongCentre,
    cardAlong:       far - leading,
    cardAlongCentre: (leading + far) / 2,
  };
}
